import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import CountUp from "react-countup";

/* ------------------------------------------------------------------
   Floating geometric "pill" shape (unchanged from the original hero).
------------------------------------------------------------------ */
function ElegantShape({
  className = "",
  delay = 0,
  width = 400,
  height = 100,
  rotate = 0,
  gradient = "from-white/[0.08]",
}) {
  const shapeRef = useRef(null);
  const innerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        shapeRef.current,
        { opacity: 0, y: -150, rotation: rotate - 15 },
        {
          opacity: 1,
          y: 0,
          rotation: rotate,
          duration: 2.4,
          delay,
          ease: "power3.out",
        }
      );

      gsap.to(innerRef.current, {
        y: 15,
        duration: 6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    });

    return () => ctx.revert();
  }, [delay, rotate]);

  return (
    <div ref={shapeRef} className={`absolute ${className}`}>
      <div ref={innerRef} style={{ width, height }} className="relative">
        <div
          className={[
            "absolute inset-0 rounded-full",
            "bg-gradient-to-r to-transparent",
            gradient,
            "backdrop-blur-[2px] border-2 border-white/[0.15]",
            "shadow-[0_8px_32px_0_rgba(255,255,255,0.1)]",
            "after:content-[''] after:absolute after:inset-0 after:rounded-full",
            "after:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2),transparent_70%)]",
          ].join(" ")}
        />
      </div>
    </div>
  );
}

/* Small mini-stat used inside the glass card's bottom grid */
function MiniStat({ value, label }) {
  return (
    <div className="flex flex-col items-center justify-center transition-transform hover:-translate-y-1 cursor-default">
      <span className="text-xl font-bold text-white sm:text-2xl">{value}</span>
      <span className="text-[10px] uppercase tracking-wider text-white/40 font-medium sm:text-xs">
        {label}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------
   Hero (now two-column: content + glassmorphism stats card)
------------------------------------------------------------------ */
const Hero = () => {
  const rootRef = useRef(null);
  const badgeRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const buttonRef = useRef(null);
  const statsCardRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "power3.out", duration: 1 },
      });

      tl.fromTo(badgeRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, delay: 0.5 })
        .fromTo(titleRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0 }, "-=0.8")
        .fromTo(subtitleRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0 }, "-=0.8")
        .fromTo(buttonRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0 }, "-=0.8")
        .fromTo(
          statsCardRef.current,
          { opacity: 0, y: 40, scale: 0.96 },
          { opacity: 1, y: 0, scale: 1, duration: 1.1, ease: "back.out(1.4)" },
          "-=0.7"
        );

      // Animate the progress bar fill once the card is in
      gsap.fromTo(
        ".hero-progress-fill",
        { width: "0%" },
        { width: "98%", duration: 1.6, delay: 1.4, ease: "power3.out" }
      );
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <header
      ref={rootRef}
      id="home"
      className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#0F0C1F]"
    >
      {/* Soft central glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-500/[0.05] via-transparent to-indigo-500/[0.05] blur-3xl" />

      {/* Floating shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <ElegantShape
          delay={0.3}
          width={600}
          height={140}
          rotate={12}
          gradient="from-violet-500/[0.15]"
          className="left-[-10%] md:left-[-5%] top-[15%] md:top-[20%]"
        />
        <ElegantShape
          delay={0.5}
          width={500}
          height={120}
          rotate={-15}
          gradient="from-indigo-500/[0.15]"
          className="right-[-5%] md:right-[0%] top-[70%] md:top-[75%]"
        />
        <ElegantShape
          delay={0.4}
          width={300}
          height={80}
          rotate={-8}
          gradient="from-fuchsia-500/[0.15]"
          className="left-[5%] md:left-[10%] bottom-[5%] md:bottom-[10%]"
        />
        <ElegantShape
          delay={0.6}
          width={200}
          height={60}
          rotate={20}
          gradient="from-cyan-500/[0.15]"
          className="right-[15%] md:right-[20%] top-[10%] md:top-[15%]"
        />
        <ElegantShape
          delay={0.7}
          width={150}
          height={40}
          rotate={-25}
          gradient="from-purple-500/[0.15]"
          className="left-[20%] md:left-[25%] top-[5%] md:top-[10%]"
        />
      </div>

      {/* Content — two-column on large screens */}
      <div className="relative z-10 container mx-auto px-4 md:px-6 py-24 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* LEFT: existing hero copy (centered on mobile, left on desktop) */}
          <div className="lg:col-span-7 text-center lg:text-left">
            {/* Badge */}
            <div
              ref={badgeRef}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] mb-8 md:mb-10"
            >
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-[#ae7fe3] to-[#244fa9]" />
              <span className="text-sm text-white/60 tracking-wide">
                InTek Solutions
              </span>
            </div>

            {/* Title */}
            <div ref={titleRef}>
              <h1 className="text-4xl sm:text-6xl md:text-7xl xl:text-8xl font-bold mb-6 md:mb-8 tracking-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-b from-white to-white/80">
                  You Dream it.
                </span>
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#ae7fe3] via-white/90 to-[#244fa9]">
                  We Build it.
                </span>
              </h1>
            </div>

            {/* Subtitle */}
            <p
              ref={subtitleRef}
              className="text-base sm:text-lg md:text-xl text-white/40 mb-8 leading-relaxed font-light tracking-wide max-w-xl mx-auto lg:mx-0 px-2 lg:px-0"
            >
              Transforming Dreams into Reality through Cutting-Edge Software
              Development.
            </p>

            {/* CTA */}
            {/* <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href="#contact1"
                ref={buttonRef}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full text-white font-medium tracking-wide bg-gradient-to-r from-[#6936F4] to-[#244fa9] shadow-[0_8px_32px_0_rgba(105,54,244,0.35)] hover:opacity-90 transition-opacity"
              >
                Get Started
              </a>
            </div> */}
          </div>

          {/* RIGHT: glassmorphism stats card (merged from the Stats section) */}
          <div className="lg:col-span-5">
            <div
              ref={statsCardRef}
              className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8 backdrop-blur-xl shadow-2xl"
            >
              {/* Card glow */}
              <div className="absolute top-0 right-0 -mr-16 -mt-16 h-64 w-64 rounded-full bg-[#6936F4]/20 blur-3xl pointer-events-none" />

              <div className="relative z-10">
                {/* Headline stat */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#6936F4] to-[#244fa9] ring-1 ring-white/20 shadow-[0_10px_25px_rgba(105,54,244,0.35)]">
                    <i className="fa-solid fa-bullseye text-white text-lg"></i>
                  </div>
                  <div>
                    <div className="text-3xl font-bold tracking-tight text-white">
                      <CountUp end={10} duration={3} enableScrollSpy scrollSpyOnce />+
                    </div>
                    <div className="text-sm text-white/50">Projects Delivered</div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="space-y-3 mb-8">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Client Satisfaction</span>
                    <span className="text-white font-medium">98%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                    <div className="hero-progress-fill h-full w-0 rounded-full bg-gradient-to-r from-[#6936F4] to-[#ae7fe3]" />
                  </div>
                </div>

                <div className="h-px w-full bg-white/10 mb-6" />

                {/* Mini stats grid — your real numbers */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <MiniStat value="10+" label="Clients" />
                  <div className="w-px h-full bg-white/10 mx-auto" />
                  <MiniStat value="5+" label="Team" />
                  <div className="w-px h-full bg-white/10 mx-auto" />
                  <MiniStat value="24/7" label="Support" />
                </div>

                {/* Status pills */}
                <div className="mt-8 flex flex-wrap gap-2">
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-medium tracking-wide text-white/70">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    AVAILABLE FOR WORK
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-medium tracking-wide text-white/70">
                    <i className="fa-solid fa-location-dot text-[#ae7fe3] text-[11px]"></i>
                    JOHANNESBURG
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top + bottom vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F0C1F] via-transparent to-[#0F0C1F]/80 pointer-events-none" />
    </header>
  );
};

export default Hero;